/**
 * myDayView.ts — Home tabs H-3b-2 (spec §4; the H-3b recon rulings, design
 * note §42): My day's pure logic — the prioritised list, its counts and time
 * estimate, and the right rail's Today / This week / 5-minute clean-up.
 * Component-free so vitest pins every rule (the homeView pattern).
 *
 * Every item is DERIVED from a read Sales Home already makes — the PENDING
 * activities, the deals list with R1's per-deal assessment, the activity
 * history (every status, for completedAt), the contacts list and the Inbound
 * queue. The deal rules read R1 as it stands (§42 ruling 4): first stage =
 * the assessment's own stage.over, quiet = R1's recentActivity "quiet" (more
 * than 14 days); the spec's own numbers (a proposal out 7 days, a close
 * within 30 days, a follow-up after 2 days) are the named constants below and
 * never appear in copy — the sentences print the real days.
 *
 * Days are LONDON days throughout (workingDays.londonDay), the server's day.
 */
import type { SalesActivity, SalesContact, SalesDeal, SalesLead } from '../../types'
import type { DealAssessment } from '../../../../api/types'
import { addWorkingDaysIso, dueDayOf, isBankHoliday, isWorkingDay, londonDay } from './workingDays'
import { dismissedFor, inboundCard, isOpenDeal, isPendingActivity, moveTarget, offDayLine, sharePct, stageDisplay, type MoveTarget } from './homeView'
import { emailState } from './contactsView'

/** Spec §4.1 rule 2: a call or meeting this many days ago with nothing since. */
export const FOLLOW_UP_AFTER_DAYS = 2
/** Spec §4.1 rule 3: a proposal out this many days with nothing logged since. */
export const PROPOSAL_CHASE_DAYS = 7
/** Spec §4.1 rule 4: a close date this many days away or fewer. */
export const CLOSING_SOON_DAYS = 30
/** Spec §4.1 time estimates in minutes, and §42 ruling 9 (meeting 5). */
export const MINUTES = { call: 5, email: 3, meeting: 5, triage: 10, dealReview: 5 } as const
/** The pipeline's first stage (the DealStage value Sales Home reasons about). */
export const FIRST_STAGE = 'New Lead'

const DAY_MS = 86_400_000
const WEEKDAY = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const plural = (n: number, one: string, many: string) => (n === 1 ? one : many)

/** Whole days from London day a to London day b (b later → positive). */
export const daysBetween = (a: string, b: string): number => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / DAY_MS)
const weekdayOf = (day: string): number => new Date(Date.parse(`${day}T00:00:00Z`)).getUTCDay()
/** "Mon 19 Oct" for a London day string — no zone can shift it. */
export function dayLabel(day: string): string {
  const d = new Date(Date.parse(`${day}T00:00:00Z`))
  return `${WEEKDAY_SHORT[d.getUTCDay()]} ${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()]}`
}
/** "19 Oct" for an ISO date or instant's first ten characters. */
const dayMonth = (iso: string) => { const d = new Date(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`)); return `${d.getUTCDate()} ${MONTH_SHORT[d.getUTCMonth()]}` }
const checkOf = (d: SalesDeal, id: DealAssessment['checks'][number]['id']) => d.assessment?.checks.find(c => c.id === id)

const TYPE_WORD: Record<SalesActivity['type'], string> = { call: 'call', email: 'email', meeting: 'meeting', note: 'note', task: 'task' }
const TYPE_LABEL: Record<SalesActivity['type'], string> = { call: 'Call', email: 'Email', meeting: 'Meeting', note: 'Note', task: 'Task' }
const TYPE_MINUTES: Partial<Record<SalesActivity['type'], number>> = { call: MINUTES.call, email: MINUTES.email, meeting: MINUTES.meeting }

/** The next working day after today (England and Wales, London days). */
export const nextWorkingDay = (today: string): string => addWorkingDaysIso(today, 1)

// ── The list ─────────────────────────────────────────────────────────────────

export type MyDayGroup = 'due' | 'follow-up' | 'quiet' | 'leads'
export const GROUP_ORDER: readonly MyDayGroup[] = ['due', 'follow-up', 'quiet', 'leads']
export type MyDayCategory = 'deals' | 'people' | 'leads'
export type MyDayTile = SalesActivity['type'] | 'deal' | 'lead'
export type MyDayAction =
  | { kind: 'move'; label: string; target: MoveTarget }
  | { kind: 'open-deal'; label: 'Open deal'; dealId: string }
  | { kind: 'log-follow-up'; label: 'Log follow-up' }
  | { kind: 'open-contacts'; label: 'Open contacts' }
  | { kind: 'open-activities'; label: 'Open activities' }
  | { kind: 'triage'; label: 'Triage' }

export interface MyDayItem {
  /** The item's identity for snooze and done: one per rule per subject. */
  key: string
  priority: 1 | 2 | 3 | 4 | 5
  group: MyDayGroup
  category: MyDayCategory
  tile: MyDayTile
  title: string
  why: string
  meta: string
  action: MyDayAction
  /** Spec §4.1's estimate for this item, null where the spec gives none (a task, a note). */
  minutes: number | null
  /** The PENDING activity a tick completes — present on activity items only (§42 ruling 6). */
  activity?: SalesActivity
  deal?: SalesDeal
  contact?: SalesContact
  /** The dedupe subject: the deal, else the contact, else the item itself. */
  subject: string
  /** Sort keys within a priority: GBP-comparable value, then age in days. */
  valuePence: number
  ageDays: number
  /** RULED 2026-10-21: every ledger family this item rests on is dismissed
   *  (homeView.dismissedFor) — held back behind "{n} dismissed. Show ›". */
  dismissed?: boolean
}

export interface MyDayInput {
  /** Home's PENDING activities (fetchSalesSummary). */
  pending: SalesActivity[]
  /** Every activity, any status — null when the read failed. */
  history: SalesActivity[] | null
  deals: SalesDeal[]
  contacts: SalesContact[] | null
  leads: SalesLead[] | null
  /** The fenced open Σ, for a proposal's share of the pipeline. */
  totalPipelinePence: number
}

const dedupeSubject = (deal: SalesDeal | undefined, contactId: string | undefined, key: string) =>
  deal ? `deal:${deal.id}` : contactId ? `contact:${contactId}` : key

/** A deal's close as the copy says it: ahead ("closes 18 Nov"), passed
 *  ("close date passed 3 Jul") or absent. */
function closeWords(d: SalesDeal, today: string): string {
  if (!d.expectedClose) return 'no close date'
  return d.expectedClose.slice(0, 10) < today ? `close date passed ${dayMonth(d.expectedClose)}` : `closes ${dayMonth(d.expectedClose)}`
}

function dealMeta(d: SalesDeal, today: string, fmt: (pence: number, currency?: string) => string): string {
  return `${fmt(d.amount, d.currency)} · ${closeWords(d, today)}`
}

/** Group 1 — pending activities due on or before the next working day; a
 *  typed meeting on a weekend or bank holiday carries Move instead (§4.1
 *  rule 1, the Up next offDayLine / moveTarget pair). */
function dueItems(input: MyDayInput, today: string, nwd: string, byDeal: Map<string, SalesDeal>, byContact: Map<string, SalesContact>): MyDayItem[] {
  const out: MyDayItem[] = []
  for (const a of input.pending) {
    if (!isPendingActivity(a)) continue
    const due = dueDayOf(a.dueAt)
    if (!due || due > nwd) continue
    const deal = a.deal ? byDeal.get(a.deal) : undefined
    const contact = a.contactId ? byContact.get(a.contactId) : undefined
    const key = `activity:${a.id}`
    const off = a.type === 'meeting' ? offDayLine(a.dueAt!) : null
    const target = off ? moveTarget(a.dueAt!) : null
    let why: string
    if (off) why = `${off}.${contact ? ` ${contact.name} is the contact.` : ''}`
    else if (due < today) { const n = daysBetween(due, today); why = `It was due ${dayLabel(due)}, ${n} ${plural(n, 'day', 'days')} ago.` }
    else if (due === today) why = "It's due today."
    else why = `It's due ${dayLabel(due)}.`
    const action: MyDayAction = target
      ? { kind: 'move', label: `Move to ${target.label}`, target }
      : deal ? { kind: 'open-deal', label: 'Open deal', dealId: deal.id } : { kind: 'open-activities', label: 'Open activities' }
    out.push({
      key, priority: 1, group: 'due', category: deal ? 'deals' : 'people', tile: a.type,
      title: a.subject, why,
      meta: [TYPE_LABEL[a.type], contact?.name, deal?.name].filter(Boolean).join(' · '),
      action, minutes: TYPE_MINUTES[a.type] ?? null, activity: a, deal, contact,
      subject: dedupeSubject(deal, a.contactId, key), valuePence: deal?.basePence ?? 0, ageDays: Math.max(0, daysBetween(due, today)),
    })
  }
  return out
}

/** The instant an activity last moved — its completion, due date or creation,
 *  whichever is latest (a row created after a call, or planned after it, is a
 *  next step). */
const latestAt = (a: SalesActivity): number => Math.max(...[a.completedAt, a.dueAt, a.createdAt].map(t => (t ? Date.parse(t) : NaN)).filter(Number.isFinite), -Infinity)

/** Group 2, rule 2 — the newest completed call or meeting per contact (else
 *  per deal), FOLLOW_UP_AFTER_DAYS or more London days ago, with no other
 *  activity (not cancelled) for that contact or deal after it. Closed deals
 *  are not followed up. The contact's email failing the shape check turns the
 *  item into the recap variant, sent to Contacts. */
function followUpItems(input: MyDayInput, today: string, byDeal: Map<string, SalesDeal>, byContact: Map<string, SalesContact>, fmt: (pence: number, currency?: string) => string, canLog: boolean): MyDayItem[] {
  if (!input.history) return []
  const rows = new Map<string, SalesActivity>()
  for (const a of [...input.history, ...input.pending]) rows.set(a.id, a)
  const all = [...rows.values()].filter(a => a.status !== 'cancelled')
  const anchorOf = (a: SalesActivity) => (a.contactId ? `contact:${a.contactId}` : a.deal ? `deal:${a.deal}` : null)
  const newest = new Map<string, SalesActivity>()
  for (const a of all) {
    if (a.status !== 'done' || (a.type !== 'call' && a.type !== 'meeting') || !a.completedAt || !Number.isFinite(Date.parse(a.completedAt))) continue
    const anchor = anchorOf(a)
    if (!anchor) continue
    const prev = newest.get(anchor)
    if (!prev || Date.parse(a.completedAt) > Date.parse(prev.completedAt!)) newest.set(anchor, a)
  }
  const out: MyDayItem[] = []
  for (const [anchor, a] of newest) {
    const doneDay = londonDay(new Date(a.completedAt!))
    const ago = daysBetween(doneDay, today)
    if (ago < FOLLOW_UP_AFTER_DAYS) continue
    const at = Date.parse(a.completedAt!)
    const later = all.some(b => b.id !== a.id && anchorOf(b) === anchor && latestAt(b) > at)
    if (later) continue
    const deal = a.deal ? byDeal.get(a.deal) : undefined
    if (deal && !isOpenDeal(deal)) continue
    const contact = a.contactId ? byContact.get(a.contactId) : undefined
    const invalid = !!contact && emailState(contact.email) === 'invalid'
    const who = contact ? `${contact.name}${contact.company ? ` · ${contact.company}` : ''}` : null
    const title = invalid && contact
      ? `Send ${contact.name} a recap`
      : who ? `Follow up ${who}` : deal ? `Follow up on ${deal.name}` : `Follow up: ${a.subject}`
    let why = `The ${TYPE_WORD[a.type]} was ${ago} ${plural(ago, 'day', 'days')} ago and nothing has been logged or planned since.`
    if (deal) why += ` ${deal.name} is ${fmt(deal.amount, deal.currency)}${deal.expectedClose ? `, ${closeWords(deal, today)}` : ''}.`
    if (invalid && contact) why += ` The saved email (${contact.email.trim()}) isn't valid, so call or fix it first.`
    const action: MyDayAction = invalid
      ? { kind: 'open-contacts', label: 'Open contacts' }
      : canLog ? { kind: 'log-follow-up', label: 'Log follow-up' }
        : deal ? { kind: 'open-deal', label: 'Open deal', dealId: deal.id } : { kind: 'open-activities', label: 'Open activities' }
    const key = `follow-up:${a.id}`
    out.push({
      key, priority: 2, group: 'follow-up', category: deal ? 'deals' : 'people', tile: a.type, title, why,
      meta: `${TYPE_LABEL[a.type]} · ${dayLabel(doneDay)}`, action, minutes: MINUTES.call, deal, contact,
      subject: dedupeSubject(deal, a.contactId, key), valuePence: deal?.basePence ?? 0, ageDays: ago,
    })
  }
  return out
}

/** Groups 2 and 3 — the deal rules over R1's assessment, first match wins per
 *  deal: a proposal out PROPOSAL_CHASE_DAYS with nothing since (3); the first
 *  stage over R1's stage threshold with nothing ever logged, a close within
 *  CLOSING_SOON_DAYS with nothing ever logged, R1's quiet (4). Each rule
 *  names the ledger families it rests on (RULED 2026-10-21): a match whose
 *  families are all dismissed falls through to the deal's next rule, and a
 *  deal with only dismissed matches yields its first one marked dismissed. */
function dealItems(input: MyDayInput, today: string, fmt: (pence: number, currency?: string) => string): MyDayItem[] {
  const out: MyDayItem[] = []
  for (const d of input.deals) {
    if (!isOpenDeal(d) || !d.assessment) continue
    const a = d.assessment
    const recent = checkOf(d, 'recentActivity'), close = checkOf(d, 'closeDate')
    const stageDays = a.stage.days
    const base = { category: 'deals' as const, tile: 'deal' as const, meta: dealMeta(d, today, fmt), action: { kind: 'open-deal' as const, label: 'Open deal' as const, dealId: d.id }, minutes: MINUTES.dealReview, deal: d, subject: `deal:${d.id}`, valuePence: d.basePence ?? 0 }
    const candidates: { item: MyDayItem; families: readonly string[] }[] = []
    const nothingSinceStage = recent?.reason === 'never_logged' || (recent?.days != null && stageDays != null && recent.days >= stageDays)
    if (d.stage === 'Proposal Sent' && stageDays != null && stageDays >= PROPOSAL_CHASE_DAYS && nothingSinceStage) {
      const share = sharePct(d.basePence, input.totalPipelinePence)
      candidates.push({ families: ['recent_activity'], item: { ...base, key: `deal:${d.id}:proposal`, priority: 3, group: 'follow-up', title: `Chase the ${d.name} proposal`,
        why: `Proposal sent ${stageDays} ${plural(stageDays, 'day', 'days')} ago, nothing logged since.${share != null ? ` ${d.name} is ${share}% of open pipeline.` : ''}`, ageDays: stageDays } })
    }
    if (d.stage === FIRST_STAGE && a.stage.over && recent?.reason === 'never_logged') {
      const n = stageDays ?? 0
      candidates.push({ families: ['stage_age', 'recent_activity'], item: { ...base, key: `deal:${d.id}:first-stage`, priority: 4, group: 'quiet', title: `Get ${d.name} moving`,
        why: `${n} ${plural(n, 'day', 'days')} in ${stageDisplay(d.stage)} and nothing logged.`, ageDays: n } })
    }
    const toClose = close?.days
    if ((close?.reason === 'close_ahead' || close?.reason === 'close_today') && toClose != null && toClose >= 0 && toClose <= CLOSING_SOON_DAYS && recent?.reason === 'never_logged') {
      candidates.push({ families: ['recent_activity'], item: { ...base, key: `deal:${d.id}:closing`, priority: 4, group: 'quiet', title: `Check in on ${d.name}`,
        why: `In ${stageDisplay(d.stage)} with nothing logged, and it closes ${toClose === 0 ? 'today' : `in ${toClose} ${plural(toClose, 'day', 'days')}`}.`, ageDays: stageDays ?? 0 } })
    }
    if (recent?.reason === 'quiet' && recent.days != null) {
      candidates.push({ families: ['recent_activity'], item: { ...base, key: `deal:${d.id}:quiet`, priority: 4, group: 'quiet', title: `Pick ${d.name} back up`,
        why: `The last logged activity was ${recent.days} ${plural(recent.days, 'day', 'days')} ago.`, ageDays: recent.days } })
    }
    if (candidates.length === 0) continue
    const live = candidates.find(c => !dismissedFor(d, c.families))
    out.push(live ? live.item : { ...candidates[0].item, dismissed: true })
  }
  return out
}

/** Group 4 — the Inbound queue as ONE item (spec §4.1), its sentence the
 *  Overview's Inbound card's (homeView.inboundCard: the same facts). */
function leadsItem(input: MyDayInput, deals: SalesDeal[], now: Date): MyDayItem | null {
  const card = input.leads ? inboundCard(input.leads, deals, now) : null
  if (!card) return null
  return {
    key: 'leads', priority: 5, group: 'leads', category: 'leads', tile: 'lead',
    title: `Triage ${card.count} inbound ${plural(card.count, 'lead', 'leads')}`, why: card.body,
    meta: `Oldest received ${dayLabel(londonDay(new Date(card.oldest.createdAt)))}`,
    action: { kind: 'triage', label: 'Triage' }, minutes: MINUTES.triage,
    subject: 'leads', valuePence: 0, ageDays: card.waitedDays,
  }
}

/** Highest priority first; within a priority by value, then by age. */
export function compareItems(a: MyDayItem, b: MyDayItem): number {
  return a.priority - b.priority || b.valuePence - a.valuePence || b.ageDays - a.ageDays || a.title.localeCompare(b.title)
}

/** Keep the first item per dedupe subject in `compareItems` order — the
 *  highest-priority item for each deal or contact (spec §4.1). */
export function dedupe(items: MyDayItem[]): MyDayItem[] {
  const seen = new Set<string>()
  const out: MyDayItem[] = []
  // A live item outranks a dismissed one for the same subject (RULED 2026-10-21).
  for (const i of [...items].sort((x, y) => Number(!!x.dismissed) - Number(!!y.dismissed) || compareItems(x, y))) {
    if (seen.has(i.subject)) continue
    seen.add(i.subject)
    out.push(i)
  }
  return out
}

/** Every item the rules generate, deduplicated and ordered — before snooze
 *  and done are applied. `canLog`: the Log Activity sheet is available. */
export function myDayItems(input: MyDayInput, now: Date, fmt: (pence: number, currency?: string) => string, canLog = true): MyDayItem[] {
  const today = londonDay(now), nwd = nextWorkingDay(today)
  const byDeal = new Map(input.deals.map(d => [d.id, d]))
  const byContact = new Map((input.contacts ?? []).map(c => [c.id, c]))
  const lead = leadsItem(input, input.deals, now)
  return dedupe([
    ...dueItems(input, today, nwd, byDeal, byContact),
    ...followUpItems(input, today, byDeal, byContact, fmt, canLog),
    ...dealItems(input, today, fmt),
    ...(lead ? [lead] : []),
  ])
}

export interface MyDayList {
  /** The visible items (snoozed and dismissed ones left out; done ones kept, struck through). */
  items: MyDayItem[]
  done: Set<string>
  total: number
  doneCount: number
  open: number
  snoozed: { until: string; keys: string[] }[]
  /** RULED 2026-10-21: items whose flags were all dismissed — out of the
   *  list, the counts, the ring and the badge; shown only on request. */
  dismissed: MyDayItem[]
}

/** Apply dismissal, snooze and done to the generated items. A done item stays
 *  in the list until the page goes (its snapshot is re-inserted after the
 *  refresh drops the completed activity), so the ring reads done/total. A
 *  dismissed item is held back first: it is neither listed nor snoozed. */
export function applyState(items: MyDayItem[], doneItems: Map<string, MyDayItem>, snoozed: Record<string, string>): MyDayList {
  const merged = dedupe([...items.filter(i => !doneItems.has(i.key)), ...doneItems.values()])
  const dismissed = merged.filter(i => i.dismissed && !doneItems.has(i.key))
  const live = merged.filter(i => !i.dismissed || doneItems.has(i.key))
  const visible = live.filter(i => !(i.key in snoozed) || doneItems.has(i.key))
  const byUntil = new Map<string, string[]>()
  for (const i of live) {
    const until = snoozed[i.key]
    if (until && !doneItems.has(i.key)) byUntil.set(until, [...(byUntil.get(until) ?? []), i.key])
  }
  const done = new Set(visible.filter(i => doneItems.has(i.key)).map(i => i.key))
  return {
    items: visible, done, total: visible.length, doneCount: done.size, open: visible.length - done.size,
    snoozed: [...byUntil.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([until, keys]) => ({ until, keys })),
    dismissed,
  }
}

export type MyDayFilter = 'all' | MyDayCategory
export const FILTERS: readonly { id: MyDayFilter; label: string }[] = [
  { id: 'all', label: 'All' }, { id: 'deals', label: 'Deals' }, { id: 'people', label: 'People' }, { id: 'leads', label: 'Leads' },
]
/** The filter's counts — OPEN items only, so All equals the switch's badge. */
export function filterCounts(list: MyDayList): Record<MyDayFilter, number> {
  const open = list.items.filter(i => !list.done.has(i.key))
  return { all: open.length, deals: open.filter(i => i.category === 'deals').length, people: open.filter(i => i.category === 'people').length, leads: open.filter(i => i.category === 'leads').length }
}

/** The group captions in spec order. Group 1 holds items due ON the next
 *  working day too, so it says "due by" that day, never "before" it (a
 *  sentence the data would contradict); "Overdue" leads it when any of its
 *  open items' due day has passed. */
export const GROUP_CAPTION = (g: MyDayGroup, nwd: string, overdue = false): string =>
  g === 'due' ? `${overdue ? 'Overdue · due by' : 'Due by'} ${dayLabel(nwd)}` : g === 'follow-up' ? 'Follow-ups' : g === 'quiet' ? 'Deals going quiet' : 'Leads'

/** True when an open activity item in the list was due before today. */
export function hasOverdue(items: MyDayItem[], done: Set<string>, today: string): boolean {
  return items.some(i => !done.has(i.key) && i.group === 'due' && !!i.activity && (dueDayOf(i.activity.dueAt) ?? today) < today)
}

// ── Copy ─────────────────────────────────────────────────────────────────────

/** The card's headline: "{n} things need you", or the All done form —
 *  "the weekend" from Friday to Sunday, else "the rest of your day". */
export function myDayHeadline(open: number, today: string): string {
  if (open > 0) return `${open} ${plural(open, 'thing needs', 'things need')} you`
  const wd = weekdayOf(today)
  return wd === 5 || wd === 6 || wd === 0 ? 'All done. Enjoy the weekend.' : 'All done. Enjoy the rest of your day.'
}

/** The sub-line's estimate over the OPEN items. Items the spec gives no
 *  estimate (a task, a note) are left out of the sum, and the line says how
 *  many it covers; null when none has one. */
export function estimateLine(items: MyDayItem[], done: Set<string>): string | null {
  const open = items.filter(i => !done.has(i.key))
  const known = open.filter(i => i.minutes != null)
  if (known.length === 0) return null
  const mins = known.reduce((s, i) => s + (i.minutes ?? 0), 0)
  return known.length === open.length
    ? `About ${mins} ${plural(mins, 'minute', 'minutes')} in total.`
    : `About ${mins} ${plural(mins, 'minute', 'minutes')} for ${known.length} of ${open.length}.`
}

/** On a day that is not a working day — and ONLY when no open item is due
 *  before the next working day — the line that says so; null otherwise. */
export function offDayNote(today: string, items: MyDayItem[], done: Set<string>): string | null {
  if (isWorkingDay(today)) return null
  const nwd = nextWorkingDay(today)
  const dueBefore = items.some(i => !done.has(i.key) && i.activity && (dueDayOf(i.activity.dueAt) ?? nwd) < nwd)
  if (dueBefore) return null
  const name = isBankHoliday(today) ? 'a bank holiday' : WEEKDAY[weekdayOf(today)]
  return `It's ${name}, so nothing on your list is due before ${WEEKDAY[weekdayOf(nwd)]}.`
}

/** The My day lede in the shared header (replaces H-3b-1's interim count). */
export function myDayLede(open: number, note: string | null): string {
  const head = open > 0 ? `${open} ${plural(open, 'thing needs', 'things need')} you.` : 'Nothing needs you right now.'
  return note ? `${head} ${note}` : head
}

export function snoozedLine(n: number, until: string): string {
  return `${n} ${plural(n, 'item', 'items')} snoozed until ${dayLabel(until)}.`
}

// ── Right rail ───────────────────────────────────────────────────────────────

const LONDON_HM = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

export interface AgendaRow { id: string; time: string; subject: string; type: SalesActivity['type'] }
/** Today's agenda: pending activities due on today's London day, by time. */
export function agenda(pending: SalesActivity[], day: string): AgendaRow[] {
  return pending
    .filter(a => isPendingActivity(a) && dueDayOf(a.dueAt) === day)
    .sort((a, b) => Date.parse(a.dueAt!) - Date.parse(b.dueAt!) || a.subject.localeCompare(b.subject))
    .map(a => ({ id: a.id, time: LONDON_HM.format(new Date(a.dueAt!)), subject: a.subject, type: a.type }))
}

/** The next working day's tile: what is planned on it, and how many snoozed
 *  items come back on it. */
export function nextDayTile(pending: SalesActivity[], nwd: string, snoozed: MyDayList['snoozed']): { label: string; weekday: string; planned: AgendaRow[]; returning: number } {
  const d = new Date(Date.parse(`${nwd}T00:00:00Z`))
  return { label: dayLabel(nwd), weekday: WEEKDAY[d.getUTCDay()], planned: agenda(pending, nwd), returning: snoozed.find(s => s.until === nwd)?.keys.length ?? 0 }
}

export function returningLine(n: number): string {
  return `${n} snoozed ${plural(n, 'item comes', 'items come')} back`
}

/** The London week (Monday to Sunday) a day falls in, as its Monday. */
export function weekStart(day: string): string {
  const wd = weekdayOf(day)
  return isoAdd(day, -((wd + 6) % 7))
}
const isoAdd = (day: string, n: number) => new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS).toISOString().slice(0, 10)

export interface WeekCounts { call: number; email: number; meeting: number }
/** This week: calls, emails and meetings COMPLETED (status done, completedAt)
 *  from this London week's Monday to today; and last week's whole total. */
export function weekCounts(history: SalesActivity[], today: string): { thisWeek: WeekCounts; thisTotal: number; lastTotal: number } {
  const mon = weekStart(today), lastMon = isoAdd(mon, -7)
  const thisWeek: WeekCounts = { call: 0, email: 0, meeting: 0 }
  let lastTotal = 0
  for (const a of history) {
    if (a.status !== 'done' || !a.completedAt || (a.type !== 'call' && a.type !== 'email' && a.type !== 'meeting')) continue
    const t = new Date(a.completedAt)
    if (Number.isNaN(t.getTime())) continue
    const day = londonDay(t)
    if (day >= mon && day <= today) thisWeek[a.type] += 1
    else if (day >= lastMon && day < mon) lastTotal += 1
  }
  return { thisWeek, thisTotal: thisWeek.call + thisWeek.email + thisWeek.meeting, lastTotal }
}

/** §42 ruling 7, verbatim shape — no "you". */
export function weekNote(lastTotal: number, thisTotal: number): string {
  return `Last week: ${lastTotal} completed. This week: ${thisTotal} so far.`
}

export interface CleanUp { unlinked: number; malformed: number; malformedExamples: string[]; unauthored: number }
/** The 5-minute clean-up's three counts: activities with no deal, contacts
 *  whose email fails the shape check (the Contacts board's emailState), and
 *  activities with no recorded author. */
export function cleanUp(history: SalesActivity[] | null, contacts: SalesContact[] | null): CleanUp {
  const bad = (contacts ?? []).filter(c => emailState(c.email) === 'invalid')
  return {
    unlinked: (history ?? []).filter(a => !a.deal).length,
    malformed: bad.length,
    malformedExamples: bad.slice(0, 2).map(c => c.email.trim()),
    unauthored: (history ?? []).filter(a => !a.createdByUserId).length,
  }
}

/** Each clean-up row as its count (the link) and the rest of its sentence. */
export const unlinkedRow = (n: number) => ({ count: `${n} ${plural(n, 'activity', 'activities')}`, rest: ` ${plural(n, "isn't", "aren't")} linked to a deal.` })
export const malformedRow = (n: number, examples: string[]) => ({
  count: `${n} ${plural(n, 'contact', 'contacts')}`,
  rest: ` ${plural(n, 'has a malformed email', 'have malformed emails')}${examples.length ? `: ${examples.join(n > examples.length ? ', ' : ' and ')}${n > examples.length ? ` and ${n - examples.length} more` : ''}` : ''}.`,
})
/** §42 ruling 8: "7 activities don't record an author, so This week can't
 *  credit them to anyone." — the count templated. */
export const unauthoredRow = (n: number) => ({
  count: `${n} ${plural(n, 'activity', 'activities')}`,
  rest: ` ${plural(n, "doesn't", "don't")} record an author, so This week can't credit ${plural(n, 'it', 'them')} to anyone.`,
})
